import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import client from "@/lib/api";

const ProviderAvailabilityContext = createContext(null);

// App-wide batching layer for streaming-availability lookups so every
// PosterCard, anywhere in the app, can show a "streaming on your service"
// badge without firing one API call per card. Cards register their title on
// mount; registrations across a short window are coalesced into a single
// /titles/watch-providers batch request. See useProviderBadge() below.
export function ProviderAvailabilityProvider({ children }) {
  const [map, setMap] = useState({});
  const pendingRef = useRef(new Set());
  const knownRef = useRef(new Set());
  const timerRef = useRef(null);

  const flush = useCallback(() => {
    const keys = Array.from(pendingRef.current);
    pendingRef.current = new Set();
    if (!keys.length) return;
    const titles = keys.map((k) => {
      const idx = k.indexOf("-");
      return { media_type: k.slice(0, idx), tmdb_id: Number(k.slice(idx + 1)) };
    });
    client.post("/titles/watch-providers", { titles })
      .then((r) => setMap((m) => ({ ...m, ...(r.data.results || {}) })))
      .catch(() => {});
  }, []);

  const register = useCallback((item) => {
    if (!item?.tmdb_id || (item.media_type !== "movie" && item.media_type !== "tv")) return;
    const key = `${item.media_type}-${item.tmdb_id}`;
    if (knownRef.current.has(key)) return;
    knownRef.current.add(key);
    pendingRef.current.add(key);
    if (timerRef.current) return;
    timerRef.current = setTimeout(() => { timerRef.current = null; flush(); }, 220);
  }, [flush]);

  return (
    <ProviderAvailabilityContext.Provider value={{ map, register }}>
      {children}
    </ProviderAvailabilityContext.Provider>
  );
}

// Returns {providers:[{name,logo_url,kind}], link} for this item once known,
// or null while pending/unavailable. Registers the item for batch lookup on
// mount as a side effect.
export function useProviderBadge(item) {
  const ctx = useContext(ProviderAvailabilityContext);
  const tmdbId = item?.tmdb_id;
  const mediaType = item?.media_type;
  useEffect(() => {
    if (ctx) ctx.register({ tmdb_id: tmdbId, media_type: mediaType });
  }, [ctx, tmdbId, mediaType]);
  if (!ctx || !tmdbId) return null;
  return ctx.map[`${mediaType}-${tmdbId}`] || null;
}
